/**
 * TUVI - Entradas de Bodega Administrativa
 * Registro completamente manual de entradas de artículos
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import './EntradasBodega.css';

const EntradasBodega = () => {
  const { success, error, warning } = useNotification();
  const [entradas, setEntradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [entradaEditando, setEntradaEditando] = useState(null);
  const [mostrarConfirmDelete, setMostrarConfirmDelete] = useState(false);
  const [entradaEliminar, setEntradaEliminar] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [archivosNuevos, setArchivosNuevos] = useState([]);
  const [archivosExistentes, setArchivosExistentes] = useState([]);
  const [archivosEliminar, setArchivosEliminar] = useState([]);
  const [mostrarVisorArchivos, setMostrarVisorArchivos] = useState(false);
  const [archivosVisor, setArchivosVisor] = useState([]);
  const [entradaVisor, setEntradaVisor] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [busquedaProv, setBusquedaProv] = useState('');
  const [busquedaProy, setBusquedaProy] = useState('');
  const [mostrarListaProv, setMostrarListaProv] = useState(false);
  const [mostrarListaProy, setMostrarListaProy] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  const [crearFactura, setCrearFactura] = useState(true);
  const [formData, setFormData] = useState({
    articulo_nombre: '',
    unidad_medida: 'unidad',
    cantidad: '',
    precio_unitario: '',
    valor_total: '',
    fecha_movimiento: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    numero_factura: '',
    observaciones: ''
  });
  const [articulosInventario, setArticulosInventario] = useState([]);
  const [mostrarListaArticulos, setMostrarListaArticulos] = useState(false);

  useEffect(() => {
    cargarEntradas();
    cargarProveedores();
    cargarProyectos();
    cargarArticulosInventario();
  }, []);

  const cargarArticulosInventario = async () => {
    try {
      const data = await client.query(`{ bodegaInventarioResumen { articulo_nombre unidad_medida stock_actual precio_promedio } }`);
      setArticulosInventario(data.bodegaInventarioResumen || []);
    } catch (err) {}
  };

  const cargarProveedores = async () => {
    try {
      const gqlP = await client.query(`{ proveedores { proveedores { id nombre nit } } }`);
      const resp = { json: async () => ({ success: true, proveedores: gqlP.proveedores.proveedores }) };
      const data = await resp.json();
      if (data.success) setProveedores(data.proveedores || []);
    } catch (err) {}
  };

  const cargarProyectos = async () => {
    try {
      const gqlPr = await client.query(`{ proyectos { id proyecto_nombre nombre_cliente apartamento } }`);
      const resp = { json: async () => ({ proyectos: gqlPr.proyectos }) };
      const data = await resp.json();
      if (data.success) setProyectos(data.proyectos || []);
    } catch (err) {}
  };

  const proveedoresFiltrados = proveedores.filter(p =>
    p.nombre?.toLowerCase().includes(busquedaProv.toLowerCase()) ||
    p.nit?.includes(busquedaProv)
  ).slice(0, 10);

  const proyectosFiltrados = proyectos.filter(p =>
    p.proyecto_nombre?.toLowerCase().includes(busquedaProy.toLowerCase()) ||
    p.apartamento?.toLowerCase().includes(busquedaProy.toLowerCase()) ||
    p.nombre_cliente?.toLowerCase().includes(busquedaProy.toLowerCase())
  ).slice(0, 10);

  const cargarEntradas = async () => {
    try {
      setLoading(true);
      const gqlE = await client.query(`{ bodegaMovimientos(tipo: "entrada") { id articulo_nombre unidad_medida tipo_movimiento cantidad precio_unitario valor_total proveedor_id proveedor_nombre proyecto_id proyecto_nombre numero_factura fecha_movimiento fecha_vencimiento observaciones usuario_registro archivos { id nombre_original ruta tipo_archivo } } }`);
      const respEntradas = { json: async () => ({ success: true, movimientos: gqlE.bodegaMovimientos }) };
      const dataEntradas = await respEntradas.json();
      if (dataEntradas.success) {
        setEntradas(dataEntradas.movimientos || []);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar entradas según búsqueda
  const entradasFiltradas = useMemo(() => {
    if (!searchTerm.trim()) return entradas;
    const termino = searchTerm.toLowerCase();
    return entradas.filter(entrada =>
      (entrada.articulo_nombre || '').toLowerCase().includes(termino) ||
      (entrada.numero_factura || '').toLowerCase().includes(termino) ||
      (entrada.proveedor_nombre || '').toLowerCase().includes(termino) ||
      (entrada.observaciones || '').toLowerCase().includes(termino)
    );
  }, [entradas, searchTerm]);

  // Cargar archivos de un movimiento
  const cargarArchivos = async (movimientoId) => {
    try {
      const gqlA = await client.query(`query($mid: Int!) { bodegaArchivos(movimiento_id: $mid) { id nombre_original tipo_archivo tamano ruta } }`, { mid: movimientoId });
      const resp = { json: async () => ({ success: true, archivos: gqlA.bodegaArchivos }) };
      const data = await resp.json();
      if (data.success) {
        return data.archivos || [];
      }
      return [];
    } catch (err) {
      console.error('Error cargando archivos:', err);
      return [];
    }
  };

  // Subir archivos al bucket "bodega" y registrar en bodega_archivos
  const subirArchivos = async (movimientoId, archivos) => {
    if (!archivos || archivos.length === 0) return true;
    try {
      const token = localStorage.getItem('tuvi_token');
      for (const archivo of archivos) {
        const fd = new FormData();
        fd.append('movimiento_id', movimientoId);
        fd.append('archivo', archivo);
        const r = await fetch(`${UPLOAD_URL}/bodega`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        if (!r.ok) console.error('Falló subida:', archivo.name, r.status);
      }
      return true;
    } catch (err) {
      console.error('Error subiendo archivos:', err);
      return false;
    }
  };

  // Eliminar archivo
  const eliminarArchivo = async (archivoId) => {
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarArchivoBodega(id: $id) { success } }`, { id: archivoId });
      return gqlR.eliminarArchivoBodega?.success;
    } catch (err) {
      console.error('Error eliminando archivo:', err);
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      const cant = parseFloat(name === 'cantidad' ? value : next.cantidad) || 0;
      const punit = parseFloat(name === 'precio_unitario' ? value : next.precio_unitario) || 0;
      const vtot = parseFloat(name === 'valor_total' ? value : next.valor_total) || 0;

      if (name === 'cantidad' || name === 'precio_unitario') {
        if (cant > 0 && punit > 0) next.valor_total = (cant * punit).toFixed(2);
      } else if (name === 'valor_total') {
        if (cant > 0 && vtot > 0) next.precio_unitario = (vtot / cant).toFixed(2);
      }
      return next;
    });
  };

  const resetForm = () => {
    setFormData({
      articulo_nombre: '',
      unidad_medida: 'unidad',
      cantidad: '',
      precio_unitario: '',
      valor_total: '',
      fecha_movimiento: new Date().toISOString().split('T')[0],
      fecha_vencimiento: '',
      numero_factura: '',
      observaciones: ''
    });
    setModoEdicion(false);
    setEntradaEditando(null);
    setArchivosNuevos([]);
    setArchivosExistentes([]);
    setArchivosEliminar([]);
    setProveedorSeleccionado(null);
    setProyectoSeleccionado(null);
    setBusquedaProv('');
    setBusquedaProy('');
    setCrearFactura(true);
    setMostrarListaArticulos(false);
  };

  const abrirModalNuevo = () => {
    resetForm();
    setMostrarForm(true);
  };

  const abrirModalEditar = async (entrada) => {
    setModoEdicion(true);
    setEntradaEditando(entrada);
    setFormData({
      articulo_nombre: entrada.articulo_nombre,
      unidad_medida: entrada.unidad_medida || 'unidad',
      cantidad: entrada.cantidad,
      precio_unitario: entrada.precio_unitario,
      valor_total: entrada.valor_total || ((parseFloat(entrada.cantidad) || 0) * (parseFloat(entrada.precio_unitario) || 0)).toFixed(2),
      fecha_movimiento: entrada.fecha_movimiento,
      fecha_vencimiento: entrada.fecha_vencimiento || '',
      numero_factura: entrada.numero_factura || '',
      observaciones: entrada.observaciones || ''
    });
    // Cargar proveedor por id; si no hay (entries antiguas), intentar por nombre
    if (entrada.proveedor_id) {
      const prov = proveedores.find(p => p.id === entrada.proveedor_id);
      if (prov) { setProveedorSeleccionado(prov); setBusquedaProv(prov.nombre); }
    } else if (entrada.proveedor_nombre) {
      const prov = proveedores.find(p =>
        (p.nombre || '').toLowerCase().trim() === (entrada.proveedor_nombre || '').toLowerCase().trim()
      );
      if (prov) { setProveedorSeleccionado(prov); setBusquedaProv(prov.nombre); }
      else setBusquedaProv(entrada.proveedor_nombre);
    }
    if (entrada.proyecto_id) {
      const proy = proyectos.find(p => p.id === entrada.proyecto_id);
      if (proy) { setProyectoSeleccionado(proy); setBusquedaProy(`${proy.proyecto_nombre} - Apto ${proy.apartamento || ''}`); }
    }
    // Cargar archivos existentes
    const archivos = await cargarArchivos(entrada.id);
    setArchivosExistentes(archivos);
    setArchivosNuevos([]);
    setArchivosEliminar([]);
    setMostrarForm(true);
  };

  const cerrarModal = () => {
    setMostrarForm(false);
    resetForm();
  };

  // Abrir visor de archivos
  const abrirVisorArchivos = async (entrada) => {
    const archivos = await cargarArchivos(entrada.id);
    setArchivosVisor(archivos);
    setEntradaVisor(entrada);
    setMostrarVisorArchivos(true);
  };

  // Manejar selección de archivos nuevos
  const handleArchivosChange = (e) => {
    const files = Array.from(e.target.files);
    setArchivosNuevos(prev => [...prev, ...files]);
  };

  // Quitar archivo nuevo de la lista
  const quitarArchivoNuevo = (index) => {
    setArchivosNuevos(prev => prev.filter((_, i) => i !== index));
  };

  // Marcar archivo existente para eliminar
  const marcarArchivoEliminar = (archivo) => {
    setArchivosEliminar(prev => [...prev, archivo.id]);
    setArchivosExistentes(prev => prev.filter(a => a.id !== archivo.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.articulo_nombre || !formData.cantidad || !formData.precio_unitario) {
      warning('Complete los campos obligatorios (Artículo, Cantidad, Precio)');
      return;
    }
    if (!proveedorSeleccionado) {
      warning('Seleccione un proveedor');
      return;
    }
    // Proyecto es opcional en entradas
    if (!formData.numero_factura) {
      warning('Ingrese el número de factura');
      return;
    }
    if (!formData.fecha_vencimiento) {
      warning('Ingrese la fecha de vencimiento');
      return;
    }
    if (!modoEdicion && archivosNuevos.length === 0) {
      warning('Debe adjuntar al menos un documento');
      return;
    }

    try {
      const movInput = {
        articulo_nombre: formData.articulo_nombre, unidad_medida: formData.unidad_medida,
        tipo_movimiento: 'entrada', cantidad: parseFloat(formData.cantidad) || 0,
        precio_unitario: parseFloat(formData.precio_unitario) || 0,
        proveedor_id: parseInt(proveedorSeleccionado.id),
        proveedor_nombre: proveedorSeleccionado.nombre,
        proyecto_id: proyectoSeleccionado?.id || null,
        proyecto_nombre: proyectoSeleccionado?.proyecto_nombre || null,
        numero_factura: formData.numero_factura || null,
        fecha_movimiento: formData.fecha_movimiento,
        fecha_vencimiento: formData.fecha_vencimiento || null,
        observaciones: formData.observaciones || null,
      };

      // Editar si estamos en modo edición; crear si es nuevo
      let mutationOk = false;
      let nuevoMovimientoId = null;
      if (modoEdicion && entradaEditando?.id) {
        const gqlR = await client.mutate(
          `mutation($id: Int!, $input: MovimientoBodegaInput!) { editarMovimientoBodega(id: $id, input: $input) { success message } }`,
          { id: entradaEditando.id, input: movInput }
        );
        mutationOk = gqlR.editarMovimientoBodega?.success;
      } else {
        const gqlR = await client.mutate(
          `mutation($input: MovimientoBodegaInput!) { crearMovimientoBodega(input: $input) { success message id } }`,
          { input: movInput }
        );
        mutationOk = gqlR.crearMovimientoBodega?.success;
        nuevoMovimientoId = gqlR.crearMovimientoBodega?.id;
      }

      if (mutationOk) {
        const movimientoId = modoEdicion ? entradaEditando.id : nuevoMovimientoId;

        // Eliminar archivos marcados
        for (const archivoId of archivosEliminar) {
          await eliminarArchivo(archivoId);
        }

        // Subir archivos nuevos
        if (archivosNuevos.length > 0) {
          const subirOk = await subirArchivos(movimientoId, archivosNuevos);
          if (!subirOk) {
            warning('Entrada guardada pero algunos archivos no se pudieron subir');
          }
        }

        // Auto-crear factura si es nueva, o si es edición SIN factura asociada (repara entradas viejas)
        let necesitaCrearFactura = !modoEdicion && crearFactura;
        if (modoEdicion && crearFactura && formData.numero_factura) {
          try {
            const r = await client.query(
              `query($prov: Int!) { facturas(proveedor_id: $prov) { facturas { id numero_factura } } }`,
              { prov: parseInt(proveedorSeleccionado.id) }
            );
            const lista = r?.facturas?.facturas || [];
            const existe = lista.some(f => String(f.numero_factura) === String(formData.numero_factura));
            necesitaCrearFactura = !existe;
          } catch {
            necesitaCrearFactura = false;
          }
        }
        if (necesitaCrearFactura) {
          try {
            const valorTotal = parseFloat(formData.cantidad) * parseFloat(formData.precio_unitario);
            const descripcionFactura = `Bodega: ${formData.articulo_nombre} (${formData.cantidad} ${formData.unidad_medida})`;

            let factData;
            if (proyectoSeleccionado) {
              // Con proyecto: factura normal asociada al proyecto
              const gqlFact = await client.mutate(
                `mutation($input: CrearFacturaInput!) { crearFactura(input: $input) { success message factura_id } }`,
                {
                  input: {
                    proveedor_id: parseInt(proveedorSeleccionado.id),
                    numero_factura: formData.numero_factura,
                    fecha_emision: formData.fecha_movimiento,
                    fecha_vencimiento: formData.fecha_vencimiento || null,
                    subtotal: valorTotal,
                    descripcion: descripcionFactura,
                    proyectos: [{ proyecto_id: proyectoSeleccionado.id, valor_asignado: valorTotal, capitulos: [] }],
                  }
                }
              );
              factData = { success: gqlFact.crearFactura.success, message: gqlFact.crearFactura.message, factura: { id: gqlFact.crearFactura.factura_id } };
            } else {
              // Sin proyecto: usar factura administrativa (queda pendiente igual)
              const gqlFact = await client.mutate(
                `mutation($input: CrearFacturaAdminInput!) { crearFacturaAdministrativa(input: $input) { success message factura_id } }`,
                {
                  input: {
                    proveedor_id: parseInt(proveedorSeleccionado.id),
                    numero_factura: formData.numero_factura,
                    fecha_emision: formData.fecha_movimiento,
                    fecha_vencimiento: formData.fecha_vencimiento || null,
                    subtotal: valorTotal,
                    iva: 0,
                    porcentaje_iva: 0,
                    descripcion: descripcionFactura,
                  }
                }
              );
              factData = { success: gqlFact.crearFacturaAdministrativa.success, message: gqlFact.crearFacturaAdministrativa.message, factura: { id: gqlFact.crearFacturaAdministrativa.factura_id } };
            }
            if (factData.success) {
              // Subir adjunto a la factura también
              if (archivosNuevos.length > 0 && factData.factura?.id) {
                const formDataFile = new FormData();
                formDataFile.append('factura_id', factData.factura.id);
                formDataFile.append('archivo', archivosNuevos[0]);
                const token = localStorage.getItem('tuvi_token');
                await fetch(`${UPLOAD_URL}/factura`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` },
                  body: formDataFile
                });
              }
              success('Entrada registrada + Factura #' + formData.numero_factura + ' creada (queda pendiente de pago)');
            } else {
              warning('Entrada registrada PERO la factura NO se creó: ' + (factData.message || 'sin mensaje'));
            }
          } catch (factErr) {
            console.error('Error factura auto:', factErr);
            warning('Entrada registrada PERO error al crear factura: ' + (factErr.message || 'desconocido'));
          }
        } else {
          success(modoEdicion ? 'Entrada actualizada exitosamente' : 'Entrada registrada exitosamente');
        }

        cerrarModal();
        cargarEntradas();
      } else {
        error('No se pudo procesar la entrada');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error al procesar la entrada');
    }
  };

  const confirmarEliminar = (entrada) => {
    setEntradaEliminar(entrada);
    setMostrarConfirmDelete(true);
  };

  const handleEliminar = async () => {
    if (!entradaEliminar) return;

    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarMovimientoBodega(id: $id) { success message } }`, { id: entradaEliminar.id });

      if (gqlR.eliminarMovimientoBodega.success) {
        success('Entrada eliminada exitosamente');
        setMostrarConfirmDelete(false);
        setEntradaEliminar(null);
        cargarEntradas();
      } else {
        error('No se pudo eliminar la entrada');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error al eliminar la entrada');
    }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatDate = (fecha) => {
    if (!fecha) return '-';
    // Anclar a mediodía Colombia para que no se corra al día anterior por timezone
    const f = String(fecha);
    const dt = new Date(f.length === 10 ? f + 'T12:00:00-05:00' : f);
    return dt.toLocaleDateString('es-CO', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="entradas-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando entradas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="entradas-container">
      {/* Header */}
      <div className="entradas-header">
        <div className="header-left">
          <h2>📥 Entradas de Bodega</h2>
          <p className="subtitle">Registro manual de entradas de artículos</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar por artículo, factura, proveedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>
          <button
            className="btn-nueva-entrada"
            onClick={abrirModalNuevo}
          >
            ➕ Nueva Entrada
          </button>
        </div>
      </div>

      {/* Formulario Modal */}
      {mostrarForm && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modoEdicion ? '✏️ Editar Entrada' : '📥 Registrar Entrada'}</h2>
              <button className="modal-close" onClick={cerrarModal}>
                ×
              </button>
            </div>

            <form className="entrada-form" onSubmit={handleSubmit}>
              <div className="form-group form-autocomplete">
                <label>Nombre del Artículo *</label>
                <input
                  type="text"
                  name="articulo_nombre"
                  value={formData.articulo_nombre}
                  onChange={(e) => {
                    handleInputChange(e);
                    setMostrarListaArticulos(true);
                  }}
                  onFocus={() => setMostrarListaArticulos(true)}
                  placeholder="Ej: Cemento Portland 50kg (escribe para sugerencias)"
                  autoComplete="off"
                  required
                />
                {mostrarListaArticulos && formData.articulo_nombre && (() => {
                  const q = formData.articulo_nombre.toLowerCase();
                  const sugerencias = articulosInventario.filter(a =>
                    a.articulo_nombre.toLowerCase().includes(q) &&
                    a.articulo_nombre.toLowerCase() !== q
                  ).slice(0, 6);
                  if (sugerencias.length === 0) return null;
                  return (
                    <div className="autocomplete-list">
                      <div style={{ padding: '6px 12px', fontSize: '0.72rem', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>
                        Artículos existentes (al usar el mismo nombre, se sumará al inventario)
                      </div>
                      {sugerencias.map((art, i) => (
                        <div key={i} className="autocomplete-item" onClick={() => {
                          setFormData(prev => ({ ...prev, articulo_nombre: art.articulo_nombre, unidad_medida: art.unidad_medida || prev.unidad_medida }));
                          setMostrarListaArticulos(false);
                        }}>
                          <strong>{art.articulo_nombre}</strong>
                          <span className="autocomplete-sub">Stock actual: {art.stock_actual} {art.unidad_medida} | Precio prom: {formatMoney(art.precio_promedio)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Proveedor con búsqueda */}
              <div className="form-group form-autocomplete">
                <label>Proveedor *</label>
                <input
                  type="text"
                  value={busquedaProv}
                  onChange={(e) => { setBusquedaProv(e.target.value); setMostrarListaProv(true); setProveedorSeleccionado(null); }}
                  onFocus={() => setMostrarListaProv(true)}
                  placeholder="Buscar proveedor..."
                />
                {mostrarListaProv && busquedaProv && proveedoresFiltrados.length > 0 && (
                  <div className="autocomplete-list">
                    {proveedoresFiltrados.map(p => (
                      <div key={p.id} className="autocomplete-item" onClick={() => {
                        setProveedorSeleccionado(p);
                        setBusquedaProv(p.nombre);
                        setMostrarListaProv(false);
                      }}>
                        <strong>{p.nombre}</strong>
                        {p.nit && <span className="autocomplete-sub">NIT: {p.nit}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Proyecto con búsqueda */}
              <div className="form-group form-autocomplete">
                <label>Proyecto</label>
                <input
                  type="text"
                  value={busquedaProy}
                  onChange={(e) => { setBusquedaProy(e.target.value); setMostrarListaProy(true); setProyectoSeleccionado(null); }}
                  onFocus={() => setMostrarListaProy(true)}
                  placeholder="Buscar proyecto..."
                />
                {mostrarListaProy && busquedaProy && proyectosFiltrados.length > 0 && (
                  <div className="autocomplete-list">
                    {proyectosFiltrados.map(p => (
                      <div key={p.id} className="autocomplete-item" onClick={() => {
                        setProyectoSeleccionado(p);
                        setBusquedaProy(`${p.proyecto_nombre} - Apto ${p.apartamento || 'S/N'}`);
                        setMostrarListaProy(false);
                      }}>
                        <strong>{p.proyecto_nombre}</strong>
                        <span className="autocomplete-sub">Apto {p.apartamento || '-'} | {p.nombre_cliente || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unidad de Medida *</label>
                  <select
                    name="unidad_medida"
                    value={formData.unidad_medida}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="unidad">Unidad</option>
                    <option value="kg">Kilogramo (kg)</option>
                    <option value="m">Metro (m)</option>
                    <option value="m2">Metro cuadrado (m²)</option>
                    <option value="m3">Metro cúbico (m³)</option>
                    <option value="litro">Litro</option>
                    <option value="galon">Galón</option>
                    <option value="bulto">Bulto</option>
                    <option value="caja">Caja</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Fecha de Entrada *</label>
                  <input
                    type="date"
                    name="fecha_movimiento"
                    value={formData.fecha_movimiento}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cantidad *</label>
                  <input
                    type="number"
                    name="cantidad"
                    value={formData.cantidad}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0.01"
                    placeholder="Ej: 10"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Precio Unitario *</label>
                  <input
                    type="number"
                    name="precio_unitario"
                    value={formData.precio_unitario}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    placeholder="Ej: 25000"
                  />
                </div>

                <div className="form-group">
                  <label>Valor Total *</label>
                  <input
                    type="number"
                    name="valor_total"
                    value={formData.valor_total}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    placeholder="Ej: 250000"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Número de Factura *</label>
                  <input
                    type="text"
                    name="numero_factura"
                    value={formData.numero_factura}
                    onChange={handleInputChange}
                    placeholder="Ej: FAC-001"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Fecha Vencimiento *</label>
                  <input
                    type="date"
                    name="fecha_vencimiento"
                    value={formData.fecha_vencimiento}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Toggle crear factura automática */}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={crearFactura}
                    onChange={(e) => setCrearFactura(e.target.checked)}
                  />
                  Crear factura automáticamente en Gestión de Pagos
                </label>
              </div>

              <div className="form-group">
                <label>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Observaciones adicionales..."
                ></textarea>
              </div>

              {/* Sección de Archivos */}
              <div className="form-group archivos-section">
                <label>📎 Documentos / Fotos</label>
                <div className="archivos-upload">
                  <input
                    type="file"
                    id="archivos-input"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleArchivosChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="archivos-input" className="btn-seleccionar-archivos">
                    📁 Seleccionar Archivos
                  </label>
                  <span className="archivos-hint">Imágenes, PDF, Word, Excel (máx. 10MB cada uno)</span>
                </div>

                {/* Archivos existentes (modo edición) */}
                {modoEdicion && archivosExistentes.length > 0 && (
                  <div className="archivos-existentes">
                    <p className="archivos-titulo">Archivos guardados:</p>
                    <div className="archivos-lista">
                      {archivosExistentes.map(archivo => (
                        <div key={archivo.id} className="archivo-item existente">
                          <span className="archivo-icono">
                            {archivo.tipo_archivo.includes('image') ? '🖼️' : '📄'}
                          </span>
                          <span className="archivo-nombre">{archivo.nombre_original}</span>
                          <a
                            href={`https://tuvisistema.com/${archivo.ruta}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ver-archivo"
                            title="Ver archivo"
                          >
                            👁️
                          </a>
                          <button
                            type="button"
                            className="btn-quitar-archivo"
                            onClick={() => marcarArchivoEliminar(archivo)}
                            title="Eliminar archivo"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Archivos nuevos por subir */}
                {archivosNuevos.length > 0 && (
                  <div className="archivos-nuevos">
                    <p className="archivos-titulo">Archivos por subir:</p>
                    <div className="archivos-lista">
                      {archivosNuevos.map((archivo, index) => (
                        <div key={index} className="archivo-item nuevo">
                          <span className="archivo-icono">
                            {archivo.type.includes('image') ? '🖼️' : '📄'}
                          </span>
                          <span className="archivo-nombre">{archivo.name}</span>
                          <span className="archivo-size">
                            ({(archivo.size / 1024).toFixed(1)} KB)
                          </span>
                          <button
                            type="button"
                            className="btn-quitar-archivo"
                            onClick={() => quitarArchivoNuevo(index)}
                            title="Quitar"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancelar"
                  onClick={cerrarModal}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-guardar">
                  {modoEdicion ? 'Guardar Cambios' : 'Registrar Entrada'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {mostrarConfirmDelete && entradaEliminar && (
        <div className="modal-overlay" onClick={() => setMostrarConfirmDelete(false)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header warning">
              <h3>⚠️ Confirmar Eliminación</h3>
              <button className="btn-cerrar" onClick={() => setMostrarConfirmDelete(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="confirm-message">
                <span className="warning-icon">🗑️</span>
                <p>¿Está seguro que desea eliminar esta entrada?</p>
                <p><strong>{entradaEliminar.articulo_nombre}</strong></p>
                <p>Cantidad: {entradaEliminar.cantidad} {entradaEliminar.unidad_medida}</p>
                <p className="warning-text">Esta acción no se puede deshacer y afectará el inventario.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setMostrarConfirmDelete(false)}>
                Cancelar
              </button>
              <button className="btn-eliminar" onClick={handleEliminar}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Visor de Archivos */}
      {mostrarVisorArchivos && entradaVisor && (
        <div className="modal-overlay" onClick={() => setMostrarVisorArchivos(false)}>
          <div className="modal-content modal-visor" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📎 Documentos de {entradaVisor.articulo_nombre}</h2>
              <button className="modal-close" onClick={() => setMostrarVisorArchivos(false)}>×</button>
            </div>
            <div className="modal-body visor-body">
              {archivosVisor.length === 0 ? (
                <div className="sin-archivos">
                  <span className="empty-icon">📭</span>
                  <p>No hay documentos adjuntos</p>
                </div>
              ) : (
                <div className="archivos-grid">
                  {archivosVisor.map(archivo => (
                    <div key={archivo.id} className="archivo-card">
                      {archivo.tipo_archivo.includes('image') ? (
                        <div className="archivo-preview imagen">
                          <img
                            src={`${UPLOAD_URL}/bodega-file/${encodeURIComponent(archivo.ruta)}`}
                            alt={archivo.nombre_original}
                          />
                        </div>
                      ) : (
                        <div className="archivo-preview documento">
                          <span className="doc-icon">📄</span>
                        </div>
                      )}
                      <div className="archivo-info">
                        <span className="archivo-nombre">{archivo.nombre_original}</span>
                        <a
                          href={`${UPLOAD_URL}/bodega-file/${encodeURIComponent(archivo.ruta)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-descargar"
                        >
                          ⬇️ Descargar
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contador de resultados */}
      {searchTerm && (
        <div className="resultados-busqueda">
          Se encontraron <strong>{entradasFiltradas.length}</strong> resultado(s) para "{searchTerm}"
        </div>
      )}

      {/* Historial de Entradas */}
      {entradas.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📥</span>
          <h3>No hay entradas registradas</h3>
          <p>Registra la primera entrada de artículos a la bodega</p>
          <button className="btn-primera" onClick={abrirModalNuevo}>
            Registrar Primera Entrada
          </button>
        </div>
      ) : entradasFiltradas.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🔍</span>
          <h3>Sin resultados</h3>
          <p>No se encontraron entradas que coincidan con "{searchTerm}"</p>
          <button className="btn-primera" onClick={() => setSearchTerm('')}>
            Limpiar Búsqueda
          </button>
        </div>
      ) : (
        <div className="tabla-wrapper">
          <table className="entradas-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Artículo</th>
                <th>Cantidad</th>
                <th>Precio Unit.</th>
                <th>Valor Total</th>
                <th>Factura</th>
                <th>Observaciones</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {entradasFiltradas.map(entrada => (
                <tr key={entrada.id}>
                  <td className="fecha">{formatDate(entrada.fecha_movimiento)}</td>
                  <td className="articulo">{entrada.articulo_nombre}</td>
                  <td className="cantidad">
                    <span className="cantidad-badge">
                      +{entrada.cantidad} {entrada.unidad_medida}
                    </span>
                  </td>
                  <td className="precio">{formatMoney(entrada.precio_unitario)}</td>
                  <td className="valor-total">{formatMoney(entrada.valor_total)}</td>
                  <td className="factura">{entrada.numero_factura || '-'}</td>
                  <td className="observaciones">{entrada.observaciones || '-'}</td>
                  <td className="acciones">
                    <button
                      className="btn-accion archivos"
                      onClick={() => abrirVisorArchivos(entrada)}
                      title="Ver documentos"
                    >
                      📎
                    </button>
                    <button
                      className="btn-accion editar"
                      onClick={() => abrirModalEditar(entrada)}
                      title="Editar entrada"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-accion eliminar"
                      onClick={() => confirmarEliminar(entrada)}
                      title="Eliminar entrada"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EntradasBodega;
